/** Promo code form <-> API payload for the Super Admin Promo codes page. Amounts are paise. */

import { endOfIstDay, formatDate, formatInr, toDateInput } from './billingAdmin';

export const SIGNUP_URL = (process.env.REACT_APP_SIGNUP_URL || 'https://inaiy.com/register').replace(/\/+$/, '');

export const PROMO_KIND_LABELS = Object.freeze({
  percent: 'Percent off',
  flat: 'Flat ₹ off',
  trial: 'Longer free trial',
});

export const EMPTY_PROMO_FORM = Object.freeze({
  code: '',
  kind: 'percent',
  percentOff: '',
  amountOffRupees: '',
  trialDays: '30',
  description: '',
  planCodes: [],
  monthly: true,
  yearly: false,
  startsOn: '',
  expiresOn: '',
  maxRedemptions: '',
  newCustomersOnly: false,
  status: 'active',
});

export function promoToForm(promo) {
  if (!promo) return { ...EMPTY_PROMO_FORM, planCodes: [] };
  const cycles = promo.billingCycles?.length ? promo.billingCycles : ['monthly'];
  return {
    code: promo.code,
    kind: promo.kind,
    percentOff: promo.percentOff != null ? String(promo.percentOff) : '',
    amountOffRupees: promo.amountOff != null ? String(promo.amountOff / 100) : '',
    trialDays: promo.trialDays != null ? String(promo.trialDays) : '',
    description: promo.description || '',
    planCodes: [...(promo.planCodes || [])],
    monthly: cycles.includes('monthly'),
    yearly: cycles.includes('yearly'),
    startsOn: toDateInput(promo.startsAt),
    expiresOn: toDateInput(promo.expiresAt),
    maxRedemptions: promo.maxRedemptions != null ? String(promo.maxRedemptions) : '',
    newCustomersOnly: Boolean(promo.newCustomersOnly),
    status: promo.status || 'active',
  };
}

function startOfIstDay(dateInput) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateInput || ''))) return null;
  const date = new Date(`${dateInput}T00:00:00.000+05:30`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function wholeNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : Number.NaN;
}

/**
 * Validated API payload, or `{ error }` with the first problem.
 * @param {typeof EMPTY_PROMO_FORM} form
 * @param {{ editing?: boolean }} [opts]
 */
export function formToPayload(form, { editing = false } = {}) {
  const payload = {
    description: form.description.trim() || null,
    startsAt: startOfIstDay(form.startsOn),
    expiresAt: endOfIstDay(form.expiresOn),
    newCustomersOnly: form.kind === 'trial' ? false : Boolean(form.newCustomersOnly),
  };
  if (!editing) {
    const code = form.code.trim().toUpperCase();
    if (!/^[A-Z0-9][A-Z0-9_-]{2,31}$/.test(code)) {
      return { error: 'Code must be 3–32 letters, numbers, - or _ (no spaces).' };
    }
    payload.code = code;
    payload.kind = form.kind;
  } else {
    payload.status = form.status;
  }

  const max = wholeNumber(form.maxRedemptions);
  if (Number.isNaN(max) || (max != null && max < 1)) return { error: 'Usage limit must be a whole number of 1 or more.' };
  payload.maxRedemptions = max;

  if (payload.startsAt && payload.expiresAt && payload.expiresAt <= payload.startsAt) {
    return { error: 'The end date must be after the start date.' };
  }

  if (form.kind === 'percent') {
    const pct = wholeNumber(form.percentOff);
    if (!(pct >= 1 && pct <= 95)) return { error: 'Percent off must be a whole number from 1 to 95.' };
    payload.percentOff = pct;
  } else if (form.kind === 'flat') {
    const rupees = Number(form.amountOffRupees);
    if (!Number.isInteger(rupees) || rupees < 1) return { error: 'Amount off must be whole rupees, at least ₹1.' };
    payload.amountOff = rupees * 100;
  } else {
    const days = wholeNumber(form.trialDays);
    if (!(days >= 15 && days <= 90)) return { error: 'Trial length must be 15 to 90 days.' };
    payload.trialDays = days;
  }

  if (form.kind !== 'trial') {
    const cycles = [form.monthly && 'monthly', form.yearly && 'yearly'].filter(Boolean);
    if (!cycles.length) return { error: 'Pick at least one billing cycle.' };
    payload.billingCycles = cycles;
    payload.planCodes = [...form.planCodes];
  }
  return { payload };
}

/** "Starter, Growth · monthly" or "All paid plans · monthly & yearly"; trial codes apply at sign-up. */
export function describeAppliesTo(promo, planNames = {}) {
  if (promo.kind === 'trial') return 'Free trial (sign-up or Billing page)';
  const plans = promo.planCodes?.length
    ? promo.planCodes.map((code) => planNames[code] || code).join(', ')
    : 'All paid plans';
  const cycles = (promo.billingCycles?.length ? promo.billingCycles : ['monthly']).join(' & ');
  return `${plans} · ${cycles}`;
}

export function describeWindow(promo, now = new Date()) {
  const starts = promo.startsAt ? new Date(promo.startsAt) : null;
  const ends = promo.expiresAt ? new Date(promo.expiresAt) : null;
  if (ends && ends <= now) return `Ended ${formatDate(ends)}`;
  if (starts && starts > now) return `Starts ${formatDate(starts)}${ends ? ` · ends ${formatDate(ends)}` : ''}`;
  return ends ? `Until ${formatDate(ends)}` : 'No end date';
}

export function describeUsage(promo) {
  const used = Number(promo.redeemedCount) || 0;
  return promo.maxRedemptions ? `${used} / ${promo.maxRedemptions}` : String(used);
}

export function describeRedemption(r) {
  if (r.kind === 'trial') return r.extraDays ? `Trial +${r.extraDays} days (to ${r.trialDays})` : 'Trial extended';
  const paid = formatInr(r.amountPaid);
  const off = formatInr(r.discountAmount);
  return `Paid ${paid} · saved ${off}${r.billingCycle ? ` · ${r.billingCycle}` : ''}`;
}

export function signupLink(code) {
  return `${SIGNUP_URL}?promo=${encodeURIComponent(code)}`;
}
